import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, Text, Platform,
} from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import {
  DndContext, useSensors, useSensor, PointerSensor, KeyboardSensor, closestCenter,
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates, arrayMove, SortableContext, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import Colors from '../../../../../constants/Colors';
import IssueRow from './components/IssueRow';
import Button from '../../../../Button';
import PlusIcon from '../../../../16pxIcons/PlusIcon';
import IssueReorderModal from './components/IssueReorderModal';
import OtherIssueRow from './components/OtherIssueRow';
import { logEvent } from '../../../../LogEvent';
import {
  TemplateIssue, TemplateIssueCategory, TicketPriority, UserGroup,
} from '../../../../../constants/Types';
import EditIcon24 from '../../../../24pxIcons/EditIcon24';
import PressableStyled from '../../../../PressableStyled';
import Switch from '../../../../Switch';
import { SortableItem } from './components/SortableItem';

interface IssueListProps {
  issues: TemplateIssue[],
  setIssues: Dispatch<SetStateAction<TemplateIssue[]>>,
  userGroups: UserGroup[],
  scrollToEnd: () => void,
  priorityDropdownShownIndex: number,
  issueTagDropdownShownIndex: number,
  tagDropdownShownIndex: number,
  setPriorityDropdownShownIndex: Dispatch<SetStateAction<number | null>>,
  setIssueTagDropdownShownIndex: Dispatch<SetStateAction<number | null>>,
  setTagDropdownShownIndex: Dispatch<SetStateAction<number>>,
  categoryDropdownShownIndex: number | null,
  setCategoryDropdownShownIndex: Dispatch<SetStateAction<number | null>>,
  slaNames: string[],
  selectedTemplateName: string,
  editName: () => void,
  photoUploadEnabled: boolean,
  setPhotoUploadEnabled: Dispatch<SetStateAction<boolean>>,
}

export const customTemplateDefaultIssues: () => TemplateIssue[] = () => [
  {
    Text: '',
    Id: 'Issue1',
    Priority: 0,
    Enabled: true,
  },
  {
    Text: '',
    Id: 'Issue2',
    Priority: 0,
    Enabled: true,
  },
  {
    Text: '',
    Id: 'Issue3',
    Priority: 0,
    Enabled: true,
  },
  {
    Text: '',
    Id: 'Issue4',
    Priority: 0,
    Enabled: true,
  },
];

const IssueList = ({
  issues, setIssues, userGroups, scrollToEnd, priorityDropdownShownIndex, tagDropdownShownIndex,
  issueTagDropdownShownIndex, setPriorityDropdownShownIndex, setIssueTagDropdownShownIndex,
  setTagDropdownShownIndex, slaNames, categoryDropdownShownIndex, setCategoryDropdownShownIndex,
  selectedTemplateName, editName, photoUploadEnabled, setPhotoUploadEnabled,
}: IssueListProps) => {
  const otherIssue: TemplateIssue | null = issues
    ?.filter((issue: TemplateIssue) => issue.Text?.toLowerCase() === 'other')[0];
  const [otherIssueEnabled, setOtherIssueEnabled] = useState<boolean>(otherIssue?.Enabled ?? false);
  const [otherIssuePriority, setOtherIssuePriority] = useState<TicketPriority>(
    otherIssue?.Priority ?? 3,
  );
  const [otherIssueTags, setOtherIssueTags] = useState<string[]>(otherIssue?.UserGroupId ?? []);
  const [reorderViewShown, setReorderViewShown] = useState<boolean>(false);
  const userGroupIds: string[] = userGroups?.map((group: UserGroup) => group.UserGroupId) ?? [];

  useEffect(() => {
    setPriorityDropdownShownIndex(null);
    setIssueTagDropdownShownIndex(null);
    const updatedIssues: TemplateIssue[] = [...issues];
    const otherIssueExists: boolean = !!otherIssue;
    if (otherIssueExists) {
      updatedIssues?.forEach((issue: TemplateIssue) => {
        if (issue.Text?.toLowerCase() === 'other') {
          issue.Priority = otherIssuePriority;
          issue.Enabled = otherIssueEnabled;
          issue.UserGroupId = otherIssueTags;
        }
      });
    } else {
      updatedIssues.push({
        Text: 'Other',
        Priority: otherIssuePriority,
        Enabled: otherIssueEnabled,
        UserGroupId: otherIssueTags,
      });
    }
    setIssues(updatedIssues);
  }, [otherIssueEnabled, otherIssuePriority, otherIssueTags]);

  useEffect(() => {
    if (priorityDropdownShownIndex === -1 || issueTagDropdownShownIndex === -1) scrollToEnd();
  }, [priorityDropdownShownIndex, issueTagDropdownShownIndex]);

  const addIssue = () => {
    const issuesToUpdate: TemplateIssue[] = [...issues];
    issuesToUpdate.push({
      Text: '',
      UserGroupId: null,
      Priority: 3,
      Enabled: true,
      LocalId: `${new Date().getMilliseconds()}`,
    });
    setIssues(issuesToUpdate);
  };

  const listIssues: TemplateIssue[] = issues
    ?.filter((issue: TemplateIssue) => issue.Text?.toLowerCase() !== 'other');
  const reorderEnabled: boolean = Platform.OS === 'web';
  const reorderButtonShown: boolean = !reorderEnabled && (listIssues?.length > 1);

  const renderItemContent = ({
    item, index, attributes, listeners, isDragging,
  }: {
    item: TemplateIssue, index: number, attributes: any,
    listeners: any, isDragging: boolean
  }) => <IssueRow
      deleteIssue={() => {
        logEvent('IssueList', 'delete_issue');
        setIssues(issues
          ?.filter((issue: TemplateIssue) => (issue.Id ? item.Id !== issue.Id : true)
            && (issue.LocalId ? item.LocalId !== issue.LocalId : true)));
      }}
      updateIssue={(
        title: string,
        tags: string[],
        priority: number,
        enabled: boolean,
        category: TemplateIssueCategory,
      ) => {
        const issuesToUpdate: TemplateIssue[] = [...issues];
        issuesToUpdate?.forEach((issue: TemplateIssue) => {
          if ((issue?.Id && item?.Id && (issue.Id === item.Id))
            || (issue?.LocalId && item?.LocalId && (issue.LocalId === item.LocalId))) {
            item.Text = title;
            item.Priority = priority;
            item.UserGroupId = tags;
            item.Enabled = enabled;
            item.Category = category;
          }
        });
        setIssues(issuesToUpdate);
      }}
      category={item.Category}
      title={item.Text}
      tags={item.UserGroupId?.filter((tag: string) => userGroupIds.includes(tag))}
      enabled={item.Enabled}
      {...{
        priorityDropdownShownIndex,
        setPriorityDropdownShownIndex,
        index,
        issueTagDropdownShownIndex,
        setIssueTagDropdownShownIndex,
        tagDropdownShownIndex,
        setTagDropdownShownIndex,
        reorderEnabled,
        slaNames,
        categoryDropdownShownIndex,
        setCategoryDropdownShownIndex,
      }}
      groups={userGroups}
      priority={parseInt(`${item.Priority}`, 10)}
      draggableProps={{ attributes, listeners, isDragging }}
    />;

  return <View style={styles.container}>
    <Text style={styles.nameLabel}>
      {'Name *'}
    </Text>
    <View style={styles.templateNameContainer}>
      <Text style={styles.initialOptionTitle}>
        {selectedTemplateName}
      </Text>
      <PressableStyled
        style={styles.editIcon}
        onPress={editName}
        testID='am-edit-template-name-btn'
      >
        <EditIcon24 />
      </PressableStyled>
    </View>
    <View style={styles.photoUploadEnabledSetting}>
      <Text style={styles.initialOptionTitle}>{'Photo upload enabled'}</Text>
      <Switch
        toggle={() => setPhotoUploadEnabled(!photoUploadEnabled)}
        toggled={photoUploadEnabled}
        style={styles.photoUploadEnabledSwitch}
        testID='photo-upload-toggle-btn'
      />
    </View>
    <Text style={[styles.initialOptionTitle, styles.subTitle]}>
      {'Issues:'}
    </Text>
    <Text style={styles.nameLabel}>
      {'These are the problems that the building occupants will report when scanning the Solve tags.'}
    </Text>
    {reorderEnabled ? <GestureHandlerRootView style={[styles.stretch, { zIndex: 2 }]}>
      <DndContext
        sensors={useSensors(
          useSensor(PointerSensor, {
            // activationConstraint: { distance: 5 },
          }),
          useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
          }),
        )}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (over && active.id !== over.id) {
            const oldIndex = listIssues.findIndex(
              (i) => (i.Id ?? i.LocalId) === active.id,
            );
            const newIndex = listIssues.findIndex(
              (i) => (i.Id ?? i.LocalId) === over.id,
            );
            const newData = arrayMove(listIssues, oldIndex, newIndex);
            setIssues([...newData, ...(otherIssue ? [otherIssue] : [])]);
          }
        }}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext
          items={listIssues.map((i) => i.Id ?? i.LocalId)}
          strategy={verticalListSortingStrategy}
        >
          {listIssues.map((item, index) => (
            <SortableItem key={item.Id ?? item.LocalId} id={item.Id ?? item.LocalId}>
              {({ attributes, listeners, isDragging }) => renderItemContent({
                item, index, attributes, listeners, isDragging,
              })}
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>
    </GestureHandlerRootView> : <View style={{ zIndex: 2 }}>
      {listIssues
        ?.map((item: TemplateIssue, index: number) => <View
          key={`issue_item_${index}`}
          style={{ zIndex: index * -1 }}
        >
          {renderItemContent({
            item, index, attributes: {}, listeners: {}, isDragging: false,
          })}
        </View>)}
    </View>}
    <Button
      title='Add issue  '
      onPress={addIssue}
      disabled={listIssues.length > 4}
      style={styles.addIssueButton}
      testID='add-issue-btn'
    >
      <PlusIcon color={Colors.light.hereworksBlue500} />
    </Button>
    {reorderButtonShown && <Button
      style={styles.reorderButton}
      onPress={() => setReorderViewShown(true)}
      title='Reorder issues'
    />}
    <View style={styles.separator} />
    <OtherIssueRow
      {...{
        priorityDropdownShownIndex,
        setPriorityDropdownShownIndex,
        issueTagDropdownShownIndex,
        setIssueTagDropdownShownIndex,
        categoryDropdownShownIndex,
        setCategoryDropdownShownIndex,
        otherIssueEnabled,
        setOtherIssueEnabled,
        otherIssuePriority,
        setOtherIssuePriority,
        userGroups,
        setOtherIssueTags,
        slaNames,
      }}
      otherIssueTags={otherIssueTags?.filter((tag: string) => userGroupIds.includes(tag)) ?? []}
    />
    {reorderButtonShown && <IssueReorderModal
      issues={issues}
      setIssues={setIssues}
      hide={() => setReorderViewShown(false)}
      visible={reorderViewShown}
    />}
  </View>;
};

const styles = StyleSheet.create({
  container: {
    marginTop: 16,
  },
  separator: {
    alignSelf: 'stretch',
    height: 1,
    marginVertical: 12,
    backgroundColor: Colors.light.hereworksBlue100,
  },
  stretch: {
    alignSelf: 'stretch',
  },
  receivingStyle: {
    backgroundColor: Colors.light.hereworksBlack50,
    borderRadius: 8,
  },
  draggingStyle: {
    backgroundColor: Colors.light.hereworksBlack100,
    borderRadius: 8,
  },
  addIssueButton: {
    alignSelf: 'stretch',
    zIndex: 1,
  },
  reorderButton: {
    marginTop: 8,
    zIndex: 1,
  },
  nameLabel: {
    fontSize: 16,
    lineHeight: Platform.OS === 'ios' ? undefined : 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 4,
  },
  editIcon: {
    marginStart: 12,
  },
  templateNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  initialOptionTitle: {
    fontSize: 20,
    lineHeight: 30,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  subTitle: {
    marginTop: 24,
  },
  photoUploadEnabledSetting: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 24,
  },
  photoUploadEnabledSwitch: {
    marginStart: 12,
  },
});

export default IssueList;
